import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import SecureTokenStorage from '../utils/tokenStorage';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  actionUrl?: string;
  actionText?: string;
  priority?: string;
  data?: Record<string, any>;
}

interface UseNotificationSocketOptions {
  onNotification?: (notification: NotificationPayload) => void;
  onUnreadCountUpdate?: (count: number) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

export function useNotificationSocket(
  options: UseNotificationSocketOptions = {}
) {
  const {
    onNotification,
    onUnreadCountUpdate,
    onConnect,
    onDisconnect,
    onError,
    enabled = true,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const connect = useCallback(() => {
    const tokens = SecureTokenStorage.getTokens();
    if (!tokens?.accessToken || !enabled) {
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(SOCKET_URL, {
      path: '/notifications-ws',
      auth: {
        token: tokens.accessToken,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      setIsConnected(true);
      setConnectionError(null);
      console.log('[NotificationSocket] Connected');
      onConnect?.();
    });

    socket.on('disconnect', (reason: string) => {
      setIsConnected(false);
      console.log('[NotificationSocket] Disconnected:', reason);
      onDisconnect?.();
    });

    socket.on('connect_error', (error: Error) => {
      setConnectionError(error.message);
      console.error('[NotificationSocket] Connection error:', error);
      onError?.(error);
    });

    socket.on('notification', (notification: NotificationPayload) => {
      console.log('[NotificationSocket] Received notification:', notification);
      onNotification?.(notification);
    });

    socket.on('unread-count', (data: { unreadCount: number }) => {
      console.log(
        '[NotificationSocket] Unread count update:',
        data.unreadCount
      );
      onUnreadCountUpdate?.(data.unreadCount);
    });

    socket.on('connected', (data: { message: string; userId: string }) => {
      console.log('[NotificationSocket] Connection confirmed:', data);
    });

    socketRef.current = socket;
  }, [
    enabled,
    onNotification,
    onUnreadCountUpdate,
    onConnect,
    onDisconnect,
    onError,
  ]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Connect when enabled and token available
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Reconnect when token changes
  useEffect(() => {
    const handleStorageChange = () => {
      if (enabled) {
        connect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [enabled, connect]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    socket: socketRef.current,
  };
}

export default useNotificationSocket;
